import React, { useEffect } from "react";
import {
  Autocomplete,
  TextField,
  Typography,
  Stack,
  Box,
  InputAdornment,
  Button,
  IconButton,
  createFilterOptions,
  alpha,
  Card,
  CardContent,
  Avatar,
  useTheme,
} from "@mui/material";
import {
  Thermostat,
  Landscape,
  Person,
  Map,
  Place,
  CameraAlt,
  Delete,
  Inventory,
  Event,
  Layers,
  LocationOn,
  Terrain,
} from "@mui/icons-material";

import { PROFIL_GRUP_OPTIONS } from "../constants/identityConstants";

const filterOptions = createFilterOptions({
  matchFrom: "start",
  ignoreCase: true,
});

// --- YARDIMCI BİLEŞENLERİ DIŞARIYA TAŞIDIK (ODAK KAYBINI ÖNLER) ---

const ModernCardHeader = ({ title, icon, color }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={2}
    sx={{
      pb: 2,
      mb: 3,
      borderBottom: "1px solid rgba(0,0,0,0.06)",
    }}
  >
    <Avatar
      variant="rounded"
      sx={{
        bgcolor: alpha(color, 0.1),
        color: color,
        width: 36,
        height: 36,
      }}
    >
      {React.cloneElement(icon, { fontSize: "small" })}
    </Avatar>

    <Typography
      variant="subtitle1"
      sx={{
        fontWeight: 800,
        color: "text.primary",
        letterSpacing: "0.5px",
        textTransform: "uppercase",
        fontSize: "0.80rem",
      }}
    >
      {title}
    </Typography>
  </Stack>
);

const ModernInputWrapper = (
  { children, hasIcon }, // hasIcon eklendi
) => (
  <Box
    sx={{
      // ... outlinedInput-root kısımları aynı kalacak
      "& .MuiInputLabel-root": {
        fontSize: "0.85rem", // hasIcon varsa: 40px sağa, 10px aşağı (Ortalama)
        // hasIcon yoksa: 14px sağa, 10px aşağı
        transform: hasIcon
          ? "translate(40px, 10px) scale(1)"
          : "translate(14px, 10px) scale(1)",
        "&.Mui-focused, &.MuiFormLabel-filled": {
          transform: "translate(14px, -9px) scale(0.75)",
        },
      },
      "& .MuiInputBase-input": {
        fontSize: "0.9rem",
        padding: "8.5px 14px",
      },
    }}
  >
    {children}
  </Box>
);

const RenderInput = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  icon,
  readOnly = false,
}) => (
  <ModernInputWrapper hasIcon={!!icon}>
    <TextField
      fullWidth
      label={label}
      name={name}
      type={type}
      value={value || ""}
      onChange={onChange}
      variant="outlined"
      InputProps={{
        readOnly: readOnly,
        startAdornment: icon ? (
          <InputAdornment
            position="start"
            sx={{ color: "action.active", mr: 0.5 }}
          >
            {icon}
          </InputAdornment>
        ) : null,
      }}
      sx={
        readOnly ? { "& .MuiOutlinedInput-root": { bgcolor: "#f5f5f5" } } : {}
      }
    />
  </ModernInputWrapper>
);

const thumbnailStyle = {
  width: "100%",
  height: 120,
  objectFit: "cover",
  borderRadius: 8,
  border: "1px solid #eee",
  cursor: "pointer",
  transition: "transform 0.2s",
  "&:hover": { transform: "scale(1.02)" },
};

// --- ANA BİLEŞEN ---

export default function IdentityTab({
  formData,
  setFormData,
  coordinators = [],
}) {
  const theme = useTheme();

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("fieldUser");
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        if (!formData.tanimlayan) {
          const defaultTanimlayan =
            parsedUser.coordinator_name || parsedUser.full_name;
          if (defaultTanimlayan) {
            setFormData((prev) => ({
              ...prev,
              tanimlayan: "",
            }));
          }
        }
      }
    } catch (error) {
      console.error("Kullanıcı verisi localStorage'dan alınamadı:", error);
    }
  }, [setFormData]); // formData dependency'sini kaldırdık ki sonsuz döngü olmasın

  const handleChange = (e) => {
    let { name, value } = e.target;
    if (typeof value === "string") value = value.toUpperCase();
    if (name === "profil_no") {
      const digits = value.replace(/\D/g, "");
      value = "P" + digits;
    }
    if (name === "yukseklik") {
      const numValue = parseFloat(value);
      if (numValue < 0 || numValue > 5137) {
        alert("Hatalı veri girişi! Rakım 0 ile 5137 m arasında olmalıdır.");
        return;
      }
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAutoChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const removeImage = (type, index = null) => {
    setFormData((prev) => {
      if (type === "horizon") return { ...prev, resim_horizon: null };
      return {
        ...prev,
        resimler_cevre: prev.resimler_cevre.filter((_, i) => i !== index),
      };
    });
  };

  // 1. Önce fonksiyonun dışına (veya component'in hemen içine) sıkıştırma yardımcısını ekle:
  const compressImage = (file, maxWidth = 800) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          // Oranları koruyarak yeniden boyutlandır
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          // %60 kalite ile JPEG olarak Base64'e çevir (Boyutu devasa oranda düşürür)
          const compressedBase64 = canvas.toDataURL("image/jpeg", 0.6);
          resolve(compressedBase64);
        };
      };
    });
  };

  // 2. Mevcut handleImageUpload fonksiyonunu bununla değiştir:
  const handleImageUpload = async (e, type) => {
    const files = Array.from(e.target.files);

    if (type === "horizon") {
      const file = files[0];
      if (file) {
        const compressedData = await compressImage(file);
        setFormData((prev) => ({ ...prev, resim_horizon: compressedData }));
      }
    }

    if (type === "cevre") {
      // Tüm seçilen dosyaları sırayla sıkıştır
      const compressedImages = await Promise.all(
        files.map((file) => compressImage(file)),
      );

      setFormData((prev) => ({
        ...prev,
        resimler_cevre: [...(prev.resimler_cevre || []), ...compressedImages],
      }));
    }
  };

  return (
    <Box
      sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 3 }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
          gap: 3,
        }}
      >
        {/* --- SOL KOLON: KİMLİK BİLGİLERİ --- */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid #eaecf0",
            overflow: "visible",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <ModernCardHeader
              title="Profil Serisi ve Kimlik"
              icon={<Landscape />}
              color="#1565c0"
            />

            <Stack spacing={3}>
              <RenderInput
                label="Çalışmanın Adı"
                name="calismanin_adi"
                value={formData.calismanin_adi}
                onChange={handleChange}
                icon={<Layers />}
                readOnly
              />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                  gap: 2,
                }}
              >
                <RenderInput
                  label="Profil No"
                  name="profil_no"
                  value={formData.profil_no}
                  onChange={handleChange}
                  icon={<Inventory />}
                />

                <RenderInput
                  label="Çuval No"
                  name="cuval_no"
                  value={formData.cuval_no}
                  onChange={handleChange}
                  icon={<Inventory />}
                />

                <RenderInput
                  label="Tarih"
                  name="tarih"
                  type="date"
                  value={formData.tarih}
                  onChange={handleChange}
                  icon={<Event />}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <ModernInputWrapper>
                  <Autocomplete
                    freeSolo
                    options={coordinators.map((c) => c.full_name)}
                    value={formData.tanimlayan || ""}
                    onChange={(e, v) => handleAutoChange("tanimlayan", v)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tanımlayan"
                        variant="outlined"
                        onChange={(e) =>
                          handleAutoChange("tanimlayan", e.target.value)
                        }
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment
                              position="start"
                              sx={{ color: "action.active", mr: 0.5 }}
                            >
                              <Person fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </ModernInputWrapper>

                <ModernInputWrapper>
                  <Autocomplete
                    freeSolo
                    options={PROFIL_GRUP_OPTIONS} // Sadece yazdığınız harfle BAŞLAYAN seçenekleri getirir
                    filterOptions={(options, state) =>
                      options.filter((item) =>
                        item
                          .toUpperCase()
                          .startsWith(state.inputValue.toUpperCase()),
                      )
                    }
                    value={formData.profilgrup || ""}
                    onChange={(e, v) => handleAutoChange("profilgrup", v)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Profil Grubu"
                        variant="outlined"
                      />
                    )}
                  />
                </ModernInputWrapper>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <RenderInput
                  label="Toprak Serisi"
                  name="toprak_serisi"
                  value={formData.toprak_serisi}
                  onChange={handleChange}
                />

                <RenderInput
                  label="Seri Kodu"
                  name="seri_kodu"
                  value={formData.seri_kodu}
                  onChange={handleChange}
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>
        {/* --- SAĞ KOLON --- */}
        <Stack spacing={3}>
          <Card
            elevation={0}
            sx={{ borderRadius: 4, border: "1px solid #eaecf0" }}
          >
            <CardContent sx={{ p: 3 }}>
              <ModernCardHeader
                title="Konum & GPS"
                icon={<Place />}
                color="#ed6c02"
              />

              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                  }}
                >
                  <RenderInput
                    label="Enlem (X)"
                    name="koordinat_x"
                    type="number"
                    value={formData.koordinat_x}
                    onChange={handleChange}
                  />

                  <RenderInput
                    label="Boylam (Y)"
                    name="koordinat_y"
                    type="number"
                    value={formData.koordinat_y}
                    onChange={handleChange}
                  />
                </Box>

                <RenderInput
                  label="Yükseklik (m)"
                  name="yukseklik"
                  type="number"
                  value={formData.yukseklik}
                  onChange={handleChange}
                  icon={<Terrain />}
                />
                {/* İlk Satır: İl ve İlçe (Zorunlu) */}

                <RenderInput
                  label="İl"
                  name="il"
                  value={formData.il}
                  onChange={handleChange}
                  icon={<Map />}
                  readOnly // İş seçiminden otomatik geliyor
                />

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                  }}
                >
                  <RenderInput
                    label="İlçe"
                    name="ilce"
                    value={formData.ilce}
                    onChange={handleChange}
                    icon={<LocationOn />}
                  />
                  {/* İkinci Satır: Mahalle (Opsiyonel) */}

                  <RenderInput
                    label="Mahalle / Köy"
                    name="mahalle"
                    value={formData.mahalle}
                    onChange={handleChange}
                    icon={<LocationOn />}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              border: "1px solid #eaecf0",
              bgcolor: "#f0fdf4",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <ModernCardHeader
                title="İklim Verileri"
                icon={<Thermostat />}
                color="#00897b"
              />

              <Stack spacing={2}>
                <RenderInput
                  label="İklim Tipi"
                  name="iklim"
                  value={formData.iklim}
                  onChange={handleChange}
                />

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                  }}
                >
                  <RenderInput
                    label="Sıcaklık (°C)"
                    name="ort_sicaklik"
                    type="number"
                    value={formData.ort_sicaklik}
                    onChange={handleChange}
                  />

                  <RenderInput
                    label="Yağış (mm)"
                    name="ort_yagis"
                    type="number"
                    value={formData.ort_yagis}
                    onChange={handleChange}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>
      {/* --- ALT BÖLÜM: FOTOĞRAFLAR --- */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          border: "1px dashed #cfd8dc",
          bgcolor: "#fafafa",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <ModernCardHeader
            title="Saha Fotoğrafları"
            icon={<CameraAlt />}
            color="#9c27b0"
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" },
              gap: 4,
            }}
          >
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                color="text.secondary"
                mb={2}
              >
                1. HORIZON (PROFİL)
              </Typography>

              {!formData.resim_horizon ? (
                <Button
                  fullWidth
                  variant="outlined"
                  component="label"
                  startIcon={<CameraAlt />}
                  sx={{ height: 120, borderStyle: "dashed", borderRadius: 3 }}
                >
                  Fotoğraf Yükle
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, "horizon")}
                  />
                </Button>
              ) : (
                <Box sx={{ position: "relative" }}>
                  <Box
                    component="img"
                    src={formData.resim_horizon}
                    sx={thumbnailStyle}
                    onClick={() => window.open(formData.resim_horizon)}
                  />

                  <IconButton
                    size="small"
                    onClick={() => removeImage("horizon")}
                    sx={{
                      position: "absolute",
                      top: -10,
                      right: -10,
                      bgcolor: "#fff",
                      boxShadow: 2,
                      "&:hover": { bgcolor: "#ffebee" },
                    }}
                  >
                    <Delete color="error" fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                color="text.secondary"
                mb={2}
              >
                2. ÇEVRE FOTOĞRAFLARI (SINIRSIZ)
              </Typography>

              {/* DİKKAT: Grid yapısı çoklu fotoğraflara uyumlu hale getirildi */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                  gap: 2,
                }}
              >
                {formData.resimler_cevre?.map((img, i) => (
                  <Box key={i} sx={{ position: "relative" }}>
                    <Box
                      component="img"
                      src={img}
                      sx={thumbnailStyle}
                      onClick={() => window.open(img)}
                    />

                    <IconButton
                      size="small"
                      onClick={() => removeImage("cevre", i)}
                      sx={{
                        position: "absolute",
                        top: -10,
                        right: -10,
                        bgcolor: "#fff",
                        boxShadow: 2,
                        "&:hover": { bgcolor: "#ffebee" },
                      }}
                    >
                      <Delete color="error" fontSize="small" />
                    </IconButton>
                  </Box>
                ))}

                {/* DİKKAT: 4 adet kontrolü kaldırıldı, buton her zaman görünür */}
                <Button
                  fullWidth
                  variant="outlined"
                  component="label"
                  startIcon={<CameraAlt />}
                  sx={{ height: 120, borderStyle: "dashed", borderRadius: 3 }}
                >
                  Çevre Ekle
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    multiple
                    onChange={(e) => handleImageUpload(e, "cevre")}
                  />
                </Button>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
